import React, { ReactElement, useEffect } from 'react'
import { ExperienceService } from '@/lib/services/experience'
import { Experience, ExperienceStatus, Widget } from '@/types'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'

import AppLayout from '@/components/app/layout/app-layout'
import Head from 'next/head'

import useCart from '@/contexts/useCart'
import { isMobile } from 'react-device-detect'
import ExperienceFromPrice from '@/components/experiences/experience-from-price'
import { ExperienceBookingSheet } from '@/components/experiences/experience-booking-sheet'
import ExperienceDetail from '@/components/experiences/experience-detail'
import { WidgetService } from '@/lib/services/widget'
import { useAnalytics } from '@/hooks/use-analytics'
import { EventName } from '@/types/analytics'
import { getLocalizedText } from '@/lib/constants/locales'
import { useRouter } from 'next/router'
import { AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'

export default function AppExperiencePage({
  experience,
  widget,
  notFound,
}: {
  experience?: Experience
  widget: Widget
  notFound?: boolean
}) {
  const { t } = useTranslation('common')
  const { idempotency } = useCart()
  const { trackExperience } = useAnalytics()
  const router = useRouter()

  // Track experience view - must be before early return (React Hooks rules)
  useEffect(() => {
    if (!idempotency || !experience) {
      return
    }

    ExperienceService.visit(experience.uuid, idempotency, isMobile)

    // Track experience view in widget
    trackExperience(EventName.ExperienceView, {
      experienceId: experience.id.toString(),
      experienceUuid: experience.uuid,
      experienceName: experience.name,
      category: experience.category?.name,
      currency: 'EUR',
      organizer: experience.organizer?.name,
      tags: experience.tags?.map((tag) => getLocalizedText(tag.label, router.locale ?? 'en')).filter((t): t is string => t !== null),
      widgetId: widget.uuid,
      widgetName: widget.name,
      sellerId: widget.company.id.toString(),
      sellerName: widget.company.name,
      platform: 'widget',
      isMobile,
    })
  }, [idempotency, experience, widget, trackExperience])

  // Show not found page if experience is missing
  if (notFound || !experience) {
    return (
      <div>
        <Head>
          <title>{t('not_found')} - {widget.company?.name}</title>
        </Head>

        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-6">
              <AlertCircle className="h-20 w-20 text-gray-400" />
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('experience_not_found')}
            </h1>

            <p className="text-lg text-gray-600 mb-8">
              {t('experience_not_found_description')}
            </p>

            <Link href={`/app/widgets/${widget.uuid}`}>
              <Button size="lg">
                {t('back_to_experiences')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // After the check above, experience is guaranteed to be defined
  const isBookable = experience.status === ExperienceStatus.Active

  return (
    <div translate="no" className="notranslate">
      <Head>
        <title>
          {experience.name} - {widget.company?.name}
        </title>
        <meta name="google" content="notranslate" />
        <meta httpEquiv="content-language" content={router.locale || 'en'} />
      </Head>
      <ExperienceDetail
        experience={experience}
        sellerId={widget.company.id}
        bookable={isBookable}
        widgetUuid={widget.uuid}
      />

      {isBookable && (
        <div className="md:hidden sticky bottom-0 bg-white w-full flex justify-between items-center py-4 border-t">
          <div>
            <ExperienceFromPrice experience={experience} />
          </div>

          <ExperienceBookingSheet
            experience={experience}
            sellerId={widget.company.id}
            bookable={isBookable}
          />
        </div>
      )}
    </div>
  )
}

AppExperiencePage.getLayout = function getLayout(page: ReactElement) {
  return <AppLayout widget={page.props.widget}>{page}</AppLayout>
}

export async function getServerSideProps({
  locale,
  params,
}: {
  locale: string
  params: Record<string, any>
}) {
  try {
    const experience = await ExperienceService.getAppExperience(
      params.uuid,
      locale
    )

    if (experience.isSimplifiedView) {
      return {
        redirect: {
          destination: `/app/widgets/${params.widgetUuid}/experiences/${params.uuid}/book-now`,
          permanent: false,
        },
      }
    }

    const widget = await WidgetService.getWidget(params.widgetUuid)

    return {
      props: {
        experience,
        widget,
        ...(await serverSideTranslations(locale, ['common', 'experience'])),
      },
    }
  } catch (error: any) {
    // If experience not found (404), still get the widget and show custom error
    if (error?.response?.status === 404) {
      try {
        const widget = await WidgetService.getWidget(params.widgetUuid)

        return {
          props: {
            widget,
            notFound: true,
            ...(await serverSideTranslations(locale, ['common', 'experience'])),
          },
        }
      } catch (widgetError) {
        // If widget also not found, use global 404
        return {
          notFound: true,
        }
      }
    }

    // For other errors, throw to show error page
    throw error
  }
}
